import { cloneDeep } from "lodash";
import { connect, useSelector } from "react-redux";
import { deviceDetect } from "react-device-detect";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";

import { api } from "@/APIRequests";
import { AssessmentEnum } from "@/core/constants";
import { BrandedHeader } from "@/components/Common/BrandedHeader/BrandedHeader";
import { ERROR_CODES } from "@/constants/errorCode";
import { featureFlagFunctionAsync } from "@/utils/featureFlag";
import { Footer } from "@/component-library/Footer";
import { getLocalTimezone } from "@/redux/reducers/authReducer";
import { OPERATIONAL_CHECK_INSURANCE_ELIGIBILITY_FLAG } from "@/constants/featureFlags";
import { showAlertWithAction } from "@/redux/actions/alerts";
import { tagReferralPath } from "@/utils/logRocketUtils";
import { trackPage } from "@/utils/EventTrackingUtil";

import { AddInsurance } from "./WorkflowSteps/AddInsurance";
import CheckEmail from "./WorkflowSteps/CheckEmail";
import ConfirmPrimaryAccountInfo from "./WorkflowSteps/ConfirmPrimaryAccountInfo";
import Eligibility from "./WorkflowSteps/Eligibility";
import PrimaryAccountInfo from "./WorkflowSteps/PrimaryAccountInfo";
import SecondaryAccountInfo from "./WorkflowSteps/SecondaryAccountInfo";
import "./style.scss";

const SelfSignUp = (props) => {
  const [showSelfSignUp, setShowSelfSignUp] = useState(true);
  const [activeStep, setActiveStep] = useState("primary");
  const [accountData, setAccountData] = useState(cloneDeep(ACCOUNT_INITIAL_VALUES));
  const [insuranceEligibilityResponse, setInsuranceEligibilityResponse] = useState({});
  const [queryParams, setQueryParams] = useState("");

  useEffect(() => {
    tagReferralPath("patient_self_signup");
  }, []);

  const primaryAccountInfo = "primary"; //base account info (name, email, password, etc.)
  const confirmPrimaryAccountInfo = "confirm_primary";
  const secondaryAccountInfo = "secondary"; //address, emergency contacts, pcp info, etc.
  const addInsurance = "add_insurance"; //new insurance component
  const insuranceEligibility = "eligibility";
  const checkEmail = "check_email";

  const publicFlags = useSelector((state) => state.flags.publicFlags);
  const publicFlagsHaveLoaded = useSelector((state) => state.flags.publicFlagsHaveLoaded);

  const baseWorkFlowProps = {
    accountData,
    insuranceEligibility,
    insuranceEligibilityResponse,
    setAccountData,
    setInsuranceEligibilityResponse,
    setShowSelfSignUp,
  };

  const changeWorkFlowStep = (activeStep) => {
    window.scrollTo(0, 0);
    setActiveStep(activeStep);
    props.history.push(`/auth/self-signup/${activeStep}${queryParams}`);
  };

  const nextStepAfterInsurance = async (isCarrierSelected) => {
    await featureFlagFunctionAsync({
      alternateBehavior: async () => {
        if (await submitAccountData()) {
          changeWorkFlowStep(checkEmail);
        }
      },
      defaultEnabled: true,
      enabled: publicFlags?.[OPERATIONAL_CHECK_INSURANCE_ELIGIBILITY_FLAG],
      enabledBehavior: async () => {
        if (isCarrierSelected) {
          changeWorkFlowStep(insuranceEligibility);
        } else if (await submitAccountData()) {
          changeWorkFlowStep(checkEmail);
        }
      },
      flagName: OPERATIONAL_CHECK_INSURANCE_ELIGIBILITY_FLAG,
      hasLoaded: publicFlagsHaveLoaded,
    });
  };

  const workflowSteps = {
    add_insurance: (
      <AddInsurance
        {...baseWorkFlowProps}
        nextStep={nextStepAfterInsurance}
        prevStep={() => changeWorkFlowStep(secondaryAccountInfo)}
      />
    ),
    check_email: <CheckEmail emailTo={accountData.email} />,
    confirm_primary: (
      <ConfirmPrimaryAccountInfo
        {...baseWorkFlowProps}
        nextStep={() => changeWorkFlowStep(secondaryAccountInfo)}
        prevStep={() => changeWorkFlowStep(primaryAccountInfo)}
      />
    ),
    eligibility: (
      <Eligibility
        {...baseWorkFlowProps}
        nextStep={async () => {
          if (await submitAccountData()) {
            changeWorkFlowStep(checkEmail);
          }
        }}
        prevStep={() => changeWorkFlowStep(secondaryAccountInfo)}
      />
    ),
    primary: (
      <PrimaryAccountInfo
        {...baseWorkFlowProps}
        history={props.history}
        nextStep={() => changeWorkFlowStep(confirmPrimaryAccountInfo)}
      />
    ),
    secondary: (
      <SecondaryAccountInfo
        {...baseWorkFlowProps}
        nextStep={() => changeWorkFlowStep(addInsurance)}
      />
    ),
  };

  const isPageReload = () => !accountData.first_name;

  const submitAccountData = async () => {
    try {
      const modifiedAccountData = accountData;

      modifiedAccountData["Username"] = accountData["email"];
      modifiedAccountData["dob"] = moment(accountData.dob, "MMDDYYYY").format("MM/DD/YYYY");
      modifiedAccountData["group_type"] = "patient_self_signup";
      const consent = modifiedAccountData["consent"];

      modifiedAccountData["referrer_domain"] = document.referrer
        ? document.referrer
            .replaceAll("http://", "")
            .replaceAll("https://", "")
            .replaceAll("localhost:3000", "")
        : null;

      const gaveNonEssentialEmailConsent = Boolean(consent?.email);

      modifiedAccountData["preferences"] = {
        email_subscriptions: {
          account_updates: true,
          marketing_communications: gaveNonEssentialEmailConsent,
          session_updates: gaveNonEssentialEmailConsent,
        },
        is_sms_enabled: Boolean(consent?.sms),
        time_zone: getLocalTimezone(),
      };

      modifiedAccountData["secondary_insurance"] = {};

      if (
        accountData.interested_in_guardianship &&
        accountData["primary_insurance"]["carrier"].length == 0
      ) {
        //if the parent doesn't have insurance, it implies they do not want to yet be a patient
        modifiedAccountData["assessment_config"] = {
          default_assessment: AssessmentEnum.A5_V2_ASSESSMENT_TYPE,
          skip_a30: true,
          skip_a5: true,
          skip_all: true,
        };
      } else {
        modifiedAccountData["assessment_config"] = {
          default_assessment: AssessmentEnum.A5_V2_ASSESSMENT_TYPE,
        };
      }

      const referralSourceOtherText = accountData["referral_source_other"]
        .trim()
        .toLowerCase()
        .replace(/ /g, "_")
        .replace(/[^a-z0-9_]/g, "");

      if (accountData["referral_source"] === "other" && referralSourceOtherText) {
        modifiedAccountData["referral_source"] = referralSourceOtherText;
      }

      if (accountData["referral_care_navigator_facility"] == "") {
        delete modifiedAccountData["referral_care_navigator_facility"];
      }

      const deviceInfo = deviceDetect();

      if (deviceInfo) {
        modifiedAccountData.registration_device_type = deviceInfo.isBrowser
          ? "Desktop"
          : "Mobile web";
      }

      setAccountData(modifiedAccountData);

      await api.auth.sign_up({ data: modifiedAccountData, showLoader: true });

      return true;
    } catch (e) {
      console.log(e);
      const error = Object.hasOwn(ERROR_CODES, e.message)
        ? ERROR_CODES[e.message]
        : ERROR_CODES["SignUpDefault"];

      props.showAlertWithAction(`${error.message} Code: ${error.code}`);

      return false;
    }
  };

  useEffect(() => {
    (async () => {
      let qp = props.location.search;

      setQueryParams(qp);
      if (
        !props.match.params.step ||
        (props.match.params.step && !Object.hasOwn(workflowSteps, props.match.params.step)) ||
        isPageReload()
      ) {
        props.history.push(`/auth/self-signup/primary${qp}`);
      } else {
        setActiveStep(props.match.params.step);
        trackPage();
      }
    })();
  }, [props.match.params.step]);

  return (
    <>
      <div className="self-signup-content">
        <div>
          <BrandedHeader className="self-signup-header" />
          <div className="self-signup-inner-content">
            {showSelfSignUp && workflowSteps[activeStep]}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default connect(null, { showAlertWithAction })(withRouter(SelfSignUp));

export const INITIAL_INSURANCE_VALUES = {
  card_back: "",
  card_front: "",
  carrier: "",
  group_id: "",
  member_id: "",
  policy_holder_name: "",
};

const ACCOUNT_INITIAL_VALUES = {
  acknowledgements: {},
  address: {
    city: "",
    state: "",
    street: "",
    zip_code: "",
  },
  confirm_password: "",
  dob: "",
  email: "",
  emergency_contact_number: "",
  emergency_contact_person: "",
  first_name: "",
  interested_in_guardianship: false,
  last_name: "",
  mobile: "",
  password: "",
  pcp_location: "",
  pcp_name: "",
  primary_insurance: cloneDeep(INITIAL_INSURANCE_VALUES),
  referral_care_navigator_facility: "",
  referral_source: "",
  referral_source_other: "",
};
